import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { toDisplayAmount, formatMoney } from '@/lib/performance/money';
import { platformsInScope, type Platform, type PlatformScope } from '@/lib/performance/types';
import { PLATFORM_LABEL, PlatformName } from '@/lib/performance/platform';
import type { PerformanceData, MetaTimeSeriesRow } from '@/lib/types';
import { cn } from '@/lib/utils';

type MetricKey = 'spend' | 'results' | 'clicks' | 'ctr';

const METRICS: { key: MetricKey; label: string }[] = [
  { key: 'spend', label: 'Cost' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'results', label: 'Conversions' },
  { key: 'ctr', label: 'CTR' },
];

// One normalised point shape for both platforms so grouping (#6276) can sum
// components and re-derive CTR per bucket instead of averaging row ratios.
interface NormPoint {
  date: string;
  spend: number;
  clicks: number;
  results: number;
  impressions: number;
}

function normGoogle(r: PerformanceData): NormPoint {
  return { date: r.date, spend: r.cost ?? 0, clicks: r.clicks ?? 0, results: r.conversions ?? 0, impressions: r.impressions ?? 0 };
}

function normMeta(r: MetaTimeSeriesRow): NormPoint {
  return {
    date: r.day,
    spend: toDisplayAmount(r.spend_cents, 'meta'),
    clicks: r.clicks ?? 0,
    results: r.leads ?? 0,
    impressions: r.impressions ?? 0,
  };
}

// Same bucketing rule as the hook's chartData: ISO Monday for weeks, first of
// the month for months.
function bucketKey(iso: string, groupBy: 'day' | 'week' | 'month'): string {
  if (groupBy === 'day') return iso;
  const d = new Date(iso);
  if (groupBy === 'week') {
    const w = new Date(d);
    w.setDate(w.getDate() - ((w.getDay() + 6) % 7));
    return w.toISOString().split('T')[0];
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function aggregate(points: NormPoint[], groupBy: 'day' | 'week' | 'month'): NormPoint[] {
  if (groupBy === 'day') return [...points].sort((a, b) => a.date.localeCompare(b.date));
  const buckets = new Map<string, NormPoint>();
  for (const p of points) {
    const key = bucketKey(p.date, groupBy);
    const b = buckets.get(key) ?? { date: key, spend: 0, clicks: 0, results: 0, impressions: 0 };
    b.spend += p.spend;
    b.clicks += p.clicks;
    b.results += p.results;
    b.impressions += p.impressions;
    buckets.set(key, b);
  }
  return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function metricValue(p: NormPoint, metric: MetricKey): number {
  switch (metric) {
    case 'spend':
      return p.spend;
    case 'results':
      return p.results;
    case 'clicks':
      return p.clicks;
    case 'ctr':
      return p.impressions > 0 ? (p.clicks / p.impressions) * 100 : 0;
  }
}

// Exported for direct unit coverage (#6282) — chart tick/tooltip formatters
// never execute under happy-dom's zero-size ResponsiveContainer.
export function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface Props {
  scope: PlatformScope;
  googleRows: PerformanceData[] | undefined;
  metaRows: MetaTimeSeriesRow[] | undefined;
  currencySymbol: string;
  /** Toolbar grouping (#6276) — buckets both platforms' series before charting. */
  groupBy: 'day' | 'week' | 'month';
}

// Metric colours for the overlay (#6305) — the platform is encoded in the line
// STYLE instead (Google solid, Meta dashed), so a metric keeps one colour
// across platforms and overlaid series stay tellable-apart.
const METRIC_COLOR: Record<MetricKey, string> = {
  spend: '#290BD9',
  clicks: '#0EA5E9',
  results: '#047857',
  ctr: '#A16207',
};

// Metrics that share a unit can honestly share an axis — the selection may span
// at most TWO unit families, one per chart side.
type UnitFamily = 'money' | 'count' | 'percent';
const METRIC_FAMILY: Record<MetricKey, UnitFamily> = {
  spend: 'money',
  clicks: 'count',
  results: 'count',
  ctr: 'percent',
};

const familyFormat = (f: UnitFamily, v: number, cs: string): string =>
  f === 'money' ? formatMoney(v, cs) : f === 'percent' ? `${v.toFixed(2)}%` : formatCount(v);

// Counts print decimals only when the value genuinely has them — small-range
// axes (e.g. conversions 0-2.5) tick at fractional steps, and rounding those
// produced duplicate labels.
const formatCount = (v: number): string =>
  Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 });

const metricFormat = (metric: MetricKey, v: number, cs: string): string =>
  metric === 'spend' ? formatMoney(v, cs) : metric === 'ctr' ? `${v.toFixed(2)}%` : formatCount(v);

/**
 * Consolidated performance-over-time chart (#5971, CP-6). The metric toggle
 * follows the Google Ads overview pattern (#6305), generalised to unit
 * families: the selection may span at most two unit families (€ / counts / %),
 * the first family owning the left axis and the second a right axis — so
 * Spend + Clicks + Conversions works (counts share the right axis), while
 * mixed magnitudes still get real scales. A pick that would need a third
 * axis evicts the oldest selections until two families fit. The platform toggle
 * picks Google, Meta, or All; under All each metric shows a solid Google line
 * and a dashed Meta line. Results stays split by name (Google Conversions vs
 * Meta Leads) — the no-false-equivalence rule.
 */
export function PerformanceOverTime({ scope, googleRows, metaRows, currencySymbol, groupBy }: Props) {
  const platforms = platformsInScope(scope);
  const [platformChoice, setPlatformChoice] = useState<Platform | 'all'>('google');
  // Ordered oldest→newest; the first metric's unit family owns the left axis.
  const [selectedMetrics, setSelectedMetrics] = useState<MetricKey[]>(['spend']);

  const toggleMetric = (key: MetricKey) =>
    setSelectedMetrics((prev) => {
      if (prev.includes(key)) {
        return prev.length > 1 ? prev.filter((k) => k !== key) : prev; // keep at least one line
      }
      let next = [...prev, key];
      // Only two chart sides: evict oldest picks until two unit families fit.
      while (new Set(next.map((k) => METRIC_FAMILY[k])).size > 2) next = next.slice(1);
      return next;
    });

  // 'all' only applies when both platforms are in scope; otherwise fall back to
  // the single in-scope platform.
  const canShowAll = platforms.length > 1;
  const choice: Platform | 'all' =
    platformChoice === 'all'
      ? canShowAll
        ? 'all'
        : platforms[0]
      : platforms.includes(platformChoice)
        ? platformChoice
        : platforms[0];
  const chartPlatforms: Platform[] = choice === 'all' ? platforms : [choice];

  // Rows carry every platform×metric as `${platform}_${metric}` so line/axis
  // selection is purely declarative. Bucketed by the toolbar grouping (#6276).
  const data = useMemo<Record<string, string | number | null>[]>(() => {
    const series: Partial<Record<Platform, NormPoint[]>> = {
      google: aggregate((googleRows ?? []).map(normGoogle), groupBy),
      meta: aggregate((metaRows ?? []).map(normMeta), groupBy),
    };
    const byDate = new Map<string, Record<string, string | number | null>>();
    for (const platform of chartPlatforms) {
      for (const p of series[platform] ?? []) {
        const row = byDate.get(p.date) ?? { date: p.date };
        for (const m of METRICS) row[`${platform}_${m.key}`] = metricValue(p, m.key);
        byDate.set(p.date, row);
      }
    }
    return Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleRows, metaRows, groupBy, choice]);

  // Selection order decides the sides: the first metric's family → left axis,
  // the first different family → right axis.
  const leftMetric = selectedMetrics[0] ?? 'spend';
  const leftFamily = METRIC_FAMILY[leftMetric];
  const rightFamily: UnitFamily | undefined = selectedMetrics
    .map((k) => METRIC_FAMILY[k])
    .find((f) => f !== leftFamily);
  // An axis shared by several metrics has no single line colour to borrow.
  const familyTint = (f: UnitFamily): string => {
    const members = selectedMetrics.filter((k) => METRIC_FAMILY[k] === f);
    return members.length === 1 ? METRIC_COLOR[members[0]] : '#94a3b8';
  };

  const seriesName = (metric: MetricKey, p: Platform) => {
    const base = metric === 'results' ? (p === 'google' ? 'Conversions' : 'Leads') : METRICS.find((m) => m.key === metric)!.label;
    return chartPlatforms.length > 1 ? `${PLATFORM_LABEL[p]} ${base}` : base;
  };

  const lines = selectedMetrics.flatMap((key) =>
    chartPlatforms.map((p) => ({
      dataKey: `${p}_${key}`,
      name: seriesName(key, p),
      stroke: METRIC_COLOR[key],
      dash: p === 'meta' ? '6 4' : undefined,
      metric: key,
    })),
  );

  const platformOptions: { value: Platform | 'all'; label: ReactNode }[] = [
    { value: 'all', label: 'All' },
    ...platforms.map((p) => ({ value: p as Platform | 'all', label: <PlatformName platform={p} /> })),
  ];

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">Performance over time</p>
        <div className="flex items-center gap-2">
          {canShowAll && <SegToggle options={platformOptions} value={choice} onChange={setPlatformChoice} />}
          <MultiSegToggle
            options={METRICS.map((m) => ({ value: m.key, label: m.label, color: METRIC_COLOR[m.key] }))}
            selected={selectedMetrics}
            onToggle={toggleMetric}
          />
        </div>
      </div>
      {data.length === 0 ? (
        <div className="flex h-56 items-center justify-center text-sm text-gray-400">
          No {choice === 'all' ? '' : `${PLATFORM_LABEL[choice]} `}data for this period
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f1f5f9"
              vertical
              // One faint guide per bucket (day/week/month) — x-axis ticks are
              // thinned by minTickGap, so the guides are generated from the data
              // count instead of the rendered ticks.
              verticalCoordinatesGenerator={({ offset }) => {
                const n = data.length;
                const o = offset as { left?: number; width?: number };
                if (!o?.width || n < 2) return [];
                return Array.from({ length: n }, (_, i) => (o.left ?? 0) + (i * o.width!) / (n - 1));
              }}
            />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={32} />
            {/* Google Ads-style dual axes, one per unit family: the first
                family owns the DEFAULT (left) axis — Recharts 3 binds the grid
                to the default axis id — and the second, when present, gets its
                own right-hand axis. Each carries its family's units; when a
                single metric owns a side, the axis borrows its line colour. */}
            <YAxis
              allowDecimals={false}
              tickFormatter={(v: number) => familyFormat(leftFamily, v, currencySymbol)}
              tick={{ fontSize: 11, fill: rightFamily ? familyTint(leftFamily) : '#94a3b8' }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            {rightFamily && (
              <YAxis
                yAxisId="right"
                orientation="right"
                allowDecimals={false}
                tickFormatter={(v: number) => familyFormat(rightFamily, v, currencySymbol)}
                tick={{ fontSize: 11, fill: familyTint(rightFamily) }}
                tickLine={false}
                axisLine={false}
                width={64}
              />
            )}
            <Tooltip
              formatter={(v, name, item) => {
                const metric = String((item as { dataKey?: string }).dataKey ?? '').split('_')[1] as MetricKey;
                return [metricFormat(metric ?? leftMetric, Number(v), currencySymbol), name];
              }}
              labelFormatter={(l) => shortDate(String(l))}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
            />
            {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {lines.map((line) => (
              <Line
                key={line.dataKey}
                type="monotone"
                dataKey={line.dataKey}
                name={line.name}
                yAxisId={METRIC_FAMILY[line.metric] === leftFamily ? undefined : 'right'}
                stroke={line.stroke}
                strokeWidth={2}
                strokeDasharray={line.dash}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function SegToggle<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'h-7 rounded px-2.5 text-xs font-medium transition-colors',
            value === o.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Multi-select flavour of SegToggle (#6305): up to two pills lit at once, each
// carrying a small checkbox in its metric's line colour so toggle ↔ chart
// correspondence is readable at a glance.
function MultiSegToggle<T extends string>({
  options,
  selected,
  onToggle,
}: {
  options: { value: T; label: ReactNode; color?: string }[];
  selected: readonly T[];
  onToggle: (v: T) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(o.value)}
            className={cn(
              'flex h-7 items-center gap-1.5 rounded px-2.5 text-xs font-medium transition-colors',
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'flex h-3 w-3 items-center justify-center rounded-[3px] border transition-colors',
                active ? 'border-transparent' : 'border-gray-300 bg-white',
              )}
              style={active ? { background: o.color ?? '#290BD9' } : undefined}
            >
              {active && (
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
